/**
 * Mindtickle series picker for the journey map builder.
 *
 * The catalog is read from Snowflake (`snowflake/mindtickle/series/`), which is also the
 * system of record for completion, so authors can only point a step at a series that the
 * nightly sync will actually be able to report on.
 *
 * Series with no modules are shown but not selectable: Mindtickle consumes a series through
 * one of its modules, so without one there is no URL to launch and the step would dead-end.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  MindtickleSeries,
  fetchMindtickleSeries,
  findMindtickleSeries,
  isMindtickleSeriesLaunchable,
  mindtickleSeriesLabel,
  mindtickleSeriesSubLabel,
} from '../../../../support/journeyMap/mindtickle';

type Props = {
  /** `content_ref_id` of the item: the Mindtickle series id. */
  value?: string | number | null;
  /** Series name stored alongside the id so the panel can label a series the API no longer returns. */
  storedSeriesName?: string;
  onSelect: (series: MindtickleSeries) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

const JourneyMapMindtickleSeriesPicker: React.FC<Props> = ({
  value = null,
  storedSeriesName = '',
  onSelect,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [catalog, setCatalog] = useState<MindtickleSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedId = value == null || value === '' ? '' : String(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchMindtickleSeries({
          search: debouncedSearch,
          limit: 500,
          signal: controller.signal,
        });

        if (active) setCatalog(rows);
      } catch (err: any) {
        if (!active || controller.signal.aborted) return;

        setCatalog([]);
        setError(
          'Unable to load the Mindtickle catalog from Snowflake. Try again, or check the Snowflake connection.',
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void run();
    return () => {
      active = false;
      controller.abort();
    };
  }, [debouncedSearch]);

  const selectedSeries = useMemo(
    () => findMindtickleSeries(catalog, selectedId),
    [catalog, selectedId],
  );

  /**
   * A series that is out of the current search results (or retired upstream) would make MUI
   * warn about an out-of-range value and silently blank the field, so only echo the value
   * back when the option is actually present.
   */
  const selectValue = selectedSeries ? selectedId : '';

  const showStoredOnly = Boolean(selectedId) && !selectedSeries && !loading;

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Search Mindtickle Series"
        size="small"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
        helperText="Leave blank to list every series from the last 12 months."
      />

      <FormControl size="small" fullWidth disabled={loading || !catalog.length}>
        <InputLabel id="mindtickle-series-label">
          {loading ? 'Loading Mindtickle Series…' : 'Mindtickle Series'}
        </InputLabel>
        <Select
          labelId="mindtickle-series-label"
          label={loading ? 'Loading Mindtickle Series…' : 'Mindtickle Series'}
          value={selectValue}
          onChange={(event) => {
            const nextId = String(event.target.value);
            const series = findMindtickleSeries(catalog, nextId);
            if (series) onSelect(series);
          }}
        >
          {catalog.map((series) => {
            const launchable = isMindtickleSeriesLaunchable(series);
            const subLabel = mindtickleSeriesSubLabel(series);

            return (
              <MenuItem key={series.series_id} value={String(series.series_id)} disabled={!launchable}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {mindtickleSeriesLabel(series)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {launchable ? subLabel : 'No published modules — cannot be launched'}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Reading the series catalog from Snowflake…
          </Typography>
        </Stack>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !error && !catalog.length ? (
        <Alert severity="info">
          {debouncedSearch
            ? `No Mindtickle series match “${debouncedSearch}”.`
            : 'No Mindtickle series were returned by Snowflake.'}
        </Alert>
      ) : null}

      {selectedSeries ? (
        <Box sx={{ px: 0.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Selected Series
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {mindtickleSeriesLabel(selectedSeries)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Series ID: {selectedSeries.series_id}
            {selectedSeries.entry_module_id ? ` · Entry module: ${selectedSeries.entry_module_id}` : ''}
          </Typography>
        </Box>
      ) : showStoredOnly ? (
        <Alert severity="warning">
          This step points at series <strong>{selectedId}</strong>
          {storedSeriesName ? ` (${storedSeriesName})` : ''}, which the Snowflake catalog no
          longer returns. The stored launch link still works; pick another series to change it.
        </Alert>
      ) : null}
    </Stack>
  );
};

export default JourneyMapMindtickleSeriesPicker;
